import { parseIsoDate } from "@lib/utils/pdp/estimatedDelivery";

const isWeekend = (date: Date): boolean => {
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
};

/**
 * Counts weekdays (Mon-Fri) strictly between `from` and `target`, so the result lines up with
 * carrier-quoted "N business days" figures. Does not account for public holidays, since none
 * of the carrier delivery-time estimates do either.
 */
export const getBusinessDaysUntil = (target: Date, from: Date = new Date()): number => {
    const start = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    const end = new Date(target.getFullYear(), target.getMonth(), target.getDate());

    if (end <= start) return 0;

    let businessDays = 0;
    const cursor = new Date(start);

    while (cursor < end) {
        cursor.setDate(cursor.getDate() + 1);
        if (!isWeekend(cursor)) businessDays += 1;
    }

    return businessDays;
};

// The inverse of getBusinessDaysUntil: walks forward `businessDays` weekdays from `from`.
// Used to turn a carrier's "N business days" promise into an actual calendar date.
export const addBusinessDays = (from: Date, businessDays: number): Date => {
    const result = new Date(from.getFullYear(), from.getMonth(), from.getDate());

    for (let remaining = businessDays; remaining > 0; ) {
        result.setDate(result.getDate() + 1);
        if (!isWeekend(result)) remaining -= 1;
    }

    return result;
};

const toIsoDateString = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

// Carrier delivery-time copy is either a single figure ("2") or a "N-M" range (e.g. "2-4").
const DELIVERY_TIME_DAYS_PATTERN = /(\d+)(?:-(\d+))?/;

// deliveryTime is backend-controlled copy, not a trusted numeric input. Real carrier estimates
// (HD, Express, PS) never quote more than a couple of weeks out, so anything past that is not
// a legitimate value. Guarding against it keeps a malformed or malicious value (e.g. a huge
// number, or Infinity-adjacent input) from making addBusinessDays spin for an unbounded number
// of iterations on the render thread.
const MAX_BUSINESS_DAYS = 14;

const parseDeliveryTimeDays = (
    deliveryTime: string | undefined
): { fastest: number; slowest: number } | null => {
    const match = deliveryTime?.match(DELIVERY_TIME_DAYS_PATTERN);
    if (!match) return null;

    const fastest = Number(match[1]);
    const slowest = match[2] ? Number(match[2]) : fastest;

    if (!Number.isSafeInteger(fastest) || !Number.isSafeInteger(slowest)) return null;
    if (slowest < fastest) return null;
    if (slowest > MAX_BUSINESS_DAYS) return null;

    return { fastest, slowest };
};

/**
 * Express only earns its place when it's actually faster than the regular estimated delivery
 * date. If express's fastest promised day is no sooner than the earliest date the SKUs in the
 * cart could arrive anyway, it offers no advantage and should be hidden.
 */
export const shouldHideExpressDelivery = (
    expressDeliveryTime: string | undefined,
    estimatedDeliveryEarliestDate: string | undefined
): boolean => {
    const expressDays = parseDeliveryTimeDays(expressDeliveryTime);
    if (!expressDays) return false;

    const earliestDate = estimatedDeliveryEarliestDate
        ? parseIsoDate(estimatedDeliveryEarliestDate)
        : null;
    if (!earliestDate) return false;

    const businessDaysUntilEarliest = getBusinessDaysUntil(earliestDate);

    return expressDays.fastest >= businessDaysUntilEarliest;
};

/**
 * Express has no cart-based estimate from the delivery-estimate API, only its own carrier
 * "N-M business days" copy - so its date is calculated directly from that, counting forward
 * from today. Returns the same { earliest, latest? } shape as pickEstimatedDeliveryDate so
 * callers can format it with formatEstimatedDeliveryDate exactly like the regular estimate.
 */
export const getExpressDeliveryEstimate = (
    expressDeliveryTime: string | undefined,
    from: Date = new Date()
): { earliest: string; latest?: string } | null => {
    const days = parseDeliveryTimeDays(expressDeliveryTime);
    if (!days) return null;

    const earliest = toIsoDateString(addBusinessDays(from, days.fastest));
    if (days.slowest === days.fastest) return { earliest };

    return { earliest, latest: toIsoDateString(addBusinessDays(from, days.slowest)) };
};
